import { Link, useParams } from 'react-router-dom';
import { projects } from '@/mocks/projects';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useReveal } from '@/hooks/useReveal';

export default function Proyecto() {
  useReveal();
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);

  if (!project) {
    return (
      <>
        <Navbar />
        <main className="pt-32 md:pt-40 min-h-[60vh] bg-white text-neutral-900">
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 text-center">
            <h1 className="font-display text-3xl font-medium tracking-tightest">
              Proyecto no encontrado
            </h1>
            <p className="text-neutral-500 mt-4">
              El proyecto que buscas no existe o fue movido.
            </p>
            <Link
              to="/proyectos"
              className="whitespace-nowrap inline-flex items-center justify-center mt-8 px-8 py-4 text-xs font-medium uppercase tracking-[0.18em] bg-neutral-900 text-white hover:bg-neutral-700 transition-colors"
            >
              Volver al portafolio
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const resultGallery = [project.image, ...project.gallery];
  const processGallery = (project as { process?: string[] }).process ?? [];

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 bg-white text-neutral-900">
        {/* Hero del proyecto */}
        <div className="relative bg-black overflow-hidden">
          <img
            src={project.image}
            alt={`${project.name} — ${project.category} en ${project.zone}`}
            title={`${project.name} · ${project.category} · San Luis Potosí`}
            className="w-full h-[55vh] md:h-[75vh] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30"></div>
          <div className="absolute inset-x-0 bottom-0 px-5 md:px-10 pb-10 md:pb-16 text-white max-w-[1600px] mx-auto">
            <p className="overline text-white/60 reveal">{project.category}</p>
            <h1 className="font-display font-medium tracking-tightest text-[clamp(2.4rem,7vw,6rem)] leading-[1.02] mt-3 reveal">
              {project.name}
            </h1>
          </div>
        </div>

        {/* Metadatos */}
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-10 md:py-14 border-b border-neutral-200 reveal">
            {[
              { label: 'Ubicación', value: project.zone },
              { label: 'Año', value: project.year },
              { label: 'Categoría', value: project.category },
              { label: 'Superficie', value: project.area },
            ].map((m) => (
              <div key={m.label}>
                <p className="overline text-neutral-400">{m.label}</p>
                <p className="font-display text-lg font-medium mt-2">{m.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 py-12 md:py-20">
            <div className="lg:col-span-5 reveal">
              <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tightest">
                Sobre el proyecto
              </h2>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-lg md:text-xl text-neutral-600 leading-relaxed">
                {project.description}
              </p>
            </div>
          </div>

          {/* Galería */}
          <div className="pb-20 md:pb-28 reveal">
            {processGallery.length > 0 && (
              <div className="flex items-baseline gap-4 border-b border-neutral-200 pb-4 mb-8 md:mb-10">
                <span className="font-display text-sm text-neutral-400">01</span>
                <h2 className="font-display text-xl md:text-2xl font-medium tracking-tightest">
                  Resultado final
                </h2>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {resultGallery.map((img, i) => (
                <div
                  key={i}
                  className={`relative overflow-hidden bg-neutral-100 ${
                    i === 0 ? 'md:col-span-2' : ''
                  }`}
                >
                  <img
                    src={img}
                    alt={`${project.name} — imagen ${i + 1}, diseño arquitectónico San Luis Potosí`}
                    className={`w-full object-cover object-top ${
                      i === 0 ? 'aspect-[16/9]' : 'aspect-[4/3]'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Proceso constructivo */}
          {processGallery.length > 0 && (
            <div className="pb-20 md:pb-28 reveal">
              <div className="flex items-baseline gap-4 border-b border-neutral-200 pb-4 mb-8 md:mb-10">
                <span className="font-display text-sm text-neutral-400">02</span>
                <h2 className="font-display text-xl md:text-2xl font-medium tracking-tightest">
                  Proceso constructivo
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {processGallery.map((img, i) => (
                  <div key={i} className="relative overflow-hidden bg-neutral-100">
                    <img
                      src={img}
                      alt={`${project.name} — proceso constructivo ${i + 1}, obra arquitectónica San Luis Potosí`}
                      className="w-full object-cover object-top aspect-[4/3]"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="border-t border-neutral-200 py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 reveal">
            <div>
              <h2 className="font-display text-2xl md:text-4xl font-medium tracking-tightest">
                ¿Tienes un proyecto similar?
              </h2>
              <p className="text-neutral-500 mt-3">
                Cuéntanos qué tienes en mente y lo platicamos.
              </p>
            </div>
            <Link
              to="/#contacto"
              className="whitespace-nowrap inline-flex items-center justify-center px-8 py-4 text-xs font-medium uppercase tracking-[0.18em] bg-neutral-900 text-white hover:bg-neutral-700 transition-colors"
            >
              Cotizar proyecto
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}